import { useEffect, useState } from "react";
import { Heart } from "lucide-react";
import { api } from "@/lib/config";
import { useAuth } from "@/lib/auth";
import { useSessionGuard } from "@/lib/sessionGuard";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

// Simple client-side cache so multiple buttons share wishlist state
let _cache = null;
let _cacheTime = 0;
const _subscribers = new Set();

async function fetchWishlist() {
  if (_cache && Date.now() - _cacheTime < 5000) return _cache;
  try {
    const { data } = await api.get("/me/wishlist");
    _cache = new Set((data || []).map((p) => p.slug));
    _cacheTime = Date.now();
  } catch {
    _cache = new Set();
    _cacheTime = Date.now();
  }
  return _cache;
}

function publish() {
  _subscribers.forEach((fn) => fn());
}

export default function WishlistButton({ slug, className = "", size = 16 }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const guard = useSessionGuard();
  const [inWishlist, setInWishlist] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) { setInWishlist(false); return; }
    let alive = true;
    const sync = async () => {
      const set = await fetchWishlist();
      if (alive) setInWishlist(set.has(slug));
    };
    sync();
    _subscribers.add(sync);
    return () => { alive = false; _subscribers.delete(sync); };
  }, [slug, user]);

  const toggle = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!user) {
      toast.info("Please sign in to save products");
      navigate("/login");
      return;
    }
    setBusy(true);
    try {
      if (inWishlist) {
        await api.delete(`/me/wishlist/${slug}`);
        _cache?.delete(slug);
        toast.success("Removed from wishlist");
      } else {
        await api.post(`/me/wishlist/${slug}`);
        _cache?.add(slug);
        toast.success("Saved to wishlist");
      }
      setInWishlist(!inWishlist);
      publish();
    } catch (err) {
      const handled = await guard(err);
      if (!handled) toast.error("Couldn't update wishlist");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      data-testid={`wish-toggle-${slug}`}
      aria-label={inWishlist ? "Remove from wishlist" : "Save to wishlist"}
      className={`inline-flex items-center justify-center rounded-full bg-white/90 backdrop-blur hover:bg-white border border-[#ece4d0] transition-all hover:scale-110 disabled:opacity-50 ${className}`}
    >
      <Heart
        width={size}
        height={size}
        strokeWidth={1.8}
        className={inWishlist ? "text-terra fill-terra" : "text-ink-muted"}
      />
    </button>
  );
}
